/**
 * Priority-Based Semantic State Resolver
 * Resolves hovered DOM element to a semantic cursor state.
 * Evaluated once per target change (delegated event), NOT per frame.
 */

export function resolveCursorTarget(target) {
  if (!target || !(target instanceof HTMLElement)) {
    return { mode: "default", label: "" };
  }

  // 1. Native Escape Hatch
  if (
    target.closest('[data-cursor="native"]') ||
    target.closest('select, option, input[type="file"], input[type="color"], input[type="range"]') ||
    target.closest('.monaco-editor, .CodeMirror, iframe')
  ) {
    return { mode: "native", label: "" };
  }

  // 2. Disabled State
  const disabledEl = target.closest(
    'button:disabled, [aria-disabled="true"], input:disabled, textarea:disabled, .disabled, [disabled]'
  );
  if (disabledEl) {
    return { mode: "disabled", label: "" };
  }

  // 3. Draggable / Drag
  const dragEl = target.closest(
    '[data-cursor="drag"], [draggable="true"], .draggable, .marquee-container'
  );
  if (dragEl) {
    return { mode: "drag", label: "DRAG ↔" };
  }

  // 4. Input Fields (Strict Native Precision / Reduced Ring)
  const inputEl = target.closest(
    'input[type="text"], input[type="email"], input[type="search"], input[type="url"], input[type="tel"], input[type="password"], textarea, [contenteditable="true"]'
  );
  if (inputEl) {
    return { mode: "input", label: "" };
  }

  // 5. Explicit data-cursor Attributes
  const explicitEl = target.closest("[data-cursor]");
  if (explicitEl) {
    const attr = explicitEl.getAttribute("data-cursor");
    switch (attr) {
      case "view":
        return { mode: "view", label: "VIEW" };
      case "open":
        return { mode: "open", label: "OPEN ↗" };
      case "explore":
        return { mode: "explore", label: "EXPLORE" };
      case "play":
        return { mode: "play", label: "PLAY ▶" };
      case "download":
        return { mode: "download", label: "GET ↓" };
      case "copy":
        return { mode: "copy", label: "COPY" };
      case "button":
        return { mode: "button", label: "" };
      case "link":
        return { mode: "link", label: "" };
      case "card":
        return { mode: "card", label: "" };
      case "text":
        return { mode: "text", label: "" };
      default:
        break;
    }
  }

  // 6. External Links (explicit target="_blank" that aren't already custom)
  const externalLink = target.closest('a[target="_blank"]');
  if (externalLink && !externalLink.hasAttribute("data-cursor")) {
    return { mode: "open", label: "OPEN ↗" };
  }

  // 7. Buttons & CTAs
  const buttonEl = target.closest(
    'button, [role="button"], .MuiButton-root, .MuiIconButton-root, input[type="submit"], input[type="button"], .btn, .button'
  );
  if (buttonEl) {
    // Check if it's a small icon button
    const isSmallIcon =
      target.closest('.MuiIconButton-root, .theme-toggle-btn, [aria-label*="close" i], .close-btn') ||
      buttonEl.offsetWidth <= 36;
    return { mode: isSmallIcon ? "icon" : "button", label: "" };
  }

  // 8. Links & Navigation
  const linkEl = target.closest('a, [role="link"], .MuiTab-root, .nav-link, .breadcrumb-item');
  if (linkEl) {
    return { mode: "link", label: "" };
  }

  // 9. Interactive Cards
  const cardEl = target.closest(
    '.MuiCard-root, .card, [data-card], .tool-card, .stats-card, .service-card'
  );
  if (cardEl && (cardEl.style.cursor === "pointer" || window.getComputedStyle(cardEl).cursor === "pointer")) {
    return { mode: "card", label: "" };
  }

  // 10. Selectable Prose / Text
  const textEl = target.closest('p, article, blockquote, .prose');
  if (textEl) {
    return { mode: "text", label: "" };
  }

  return { mode: "default", label: "" };
}
